import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import { GroundcoverLog, LOG_LIMIT } from '../../../connection/health';
import { LOG_WINDOWS, UseGroundcoverLogsResult } from '../../../hooks/health/use-groundcover-logs';
import { formatAgo, formatTimestamp } from '../format';
import Chip from '../chip';

interface Props {
	logs: UseGroundcoverLogsResult;
	windowMs: number;
	onWindowChange: (ms: number) => void;
}

const MAX_LINE_LENGTH = 180;

/** `line` is what the API returns; the rest are fallbacks. */
const lineOf = (log: GroundcoverLog): string => String(
	log.line ?? log.content ?? log.body ?? log.message ?? '',
);

const truncate = (line: string): string => (
	line.length > MAX_LINE_LENGTH ? `${line.slice(0, MAX_LINE_LENGTH)}…` : line
);

const levelOf = (log: GroundcoverLog): string => String(log.level ?? '').toLowerCase();

/** The API says `warning`; `warn` alone matched a level nothing ever sends. */
const isWarnLevel = (level: string): boolean => level === 'warning' || level === 'warn';

/** Not per room: a fault across rooms is a different problem from one room. */
export default function LogsPanel({ logs, windowMs, onWindowChange }: Props) {
	const [level, setLevel] = useState<string>('');

	// Derived, not hardcoded: the API returns `warning` and `unknown`, which a
	// fixed ['error','warn','info'] list silently failed to match.
	const counts = useMemo(() => logs.logs.reduce((acc, log) => {
		const name = levelOf(log) || 'unknown';
		return { ...acc, [name]: (acc[name] ?? 0) + 1 };
	}, {} as Record<string, number>), [logs.logs]);

	// Errors first, then alphabetical.
	const levels = useMemo(() => Object.keys(counts).sort((a, b) => {
		if (a === 'error') return -1;
		if (b === 'error') return 1;
		return a.localeCompare(b);
	}), [counts]);

	const visible = useMemo(
		() => (level ? logs.logs.filter((log) => levelOf(log) === level) : logs.logs),
		[logs.logs, level],
	);

	return (
		<section className='health-panel'>
			<header className='health-panel__header'>
				<div>
					<h3 className='health-panel__title'>Browser logs</h3>
					<span className='health-panel__source'>groundcover</span>
				</div>
				<div className='health-panel__status'>
					<span className={classNames('health-pill', counts.error ? 'health-pill--error' : 'health-pill--ok')}>
						{counts.error ? `${counts.error} errors` : `${logs.logs.length} lines`}
					</span>
					<button type='button' className='health-link-button' onClick={logs.refresh}>
						{logs.fetchedAt ? `queried ${formatAgo(logs.fetchedAt)}` : 'querying…'}
					</button>
				</div>
			</header>

			<div className='health-panel__body'>
				<div className='health-log__filters'>
					<div className='health-chips'>
						{LOG_WINDOWS.map(({ label, ms }) => (
							<button
								key={label}
								type='button'
								className='health-chip-button'
								onClick={() => onWindowChange(ms)}
							>
								<Chip label={label} tone={ms === windowMs ? 'on' : undefined} />
							</button>
						))}
					</div>

					{!!logs.logs.length && (
						<div className='health-chips health-log__levels'>
							<Chip label='all' value={logs.logs.length} />
							{levels.map((name) => (
								<button
									key={name}
									type='button'
									className='health-chip-button'
									onClick={() => setLevel(level === name ? '' : name)}
								>
									<Chip
										label={name}
										value={counts[name]}
										tone={name === 'error' || isWarnLevel(name) ? 'warn' : undefined}
										note={level === name ? 'filtered' : undefined}
									/>
								</button>
							))}
						</div>
					)}
				</div>

				{!logs.loaded && <p className='health-panel__note'>Querying groundcover…</p>}

				{!!logs.error && (
					<p className='health-banner health-banner--error'>
						Could not query logs: {logs.error}
					</p>
				)}

				{logs.loaded && !logs.error && !logs.logs.length && (
					<p className='health-panel__note'>
						No logs for this room in the selected window — try a wider one.
					</p>
				)}

				{!!logs.logs.length && (
					<>
						{logs.limitReached && (
							<p className='health-subtle'>
								Hit the {LOG_LIMIT} line limit — this is the newest part of the
								window, not all of it. Narrow the window to see further back.
							</p>
						)}

						<div className='health-log__body'>
							<div className='health-log__head'>
								<span className='health-log__time'>Time</span>
								<span className='health-log__level'>Level</span>
								<span className='health-log__env'>Env</span>
								<span className='health-log__message'>Message</span>
							</div>

							<ol className='health-log__list'>
								{visible.map((log, index) => (
									<li key={`${log.timestamp ?? index}-${index}`}>
										<span className='health-log__time health-mono'>
											{log.timestamp ? formatTimestamp(Date.parse(log.timestamp)) : '—'}
										</span>
										<span className={classNames('health-log__level', {
											'health-log__level--error': levelOf(log) === 'error',
											'health-log__level--warn': isWarnLevel(levelOf(log)),
										})}
										>
											{levelOf(log) || 'unknown'}
										</span>
										<span className='health-log__env'>
											{log.env ? String(log.env) : '—'}
										</span>
										<span className='health-log__message' title={lineOf(log)}>
											{truncate(lineOf(log))}
										</span>
									</li>
								))}
							</ol>
						</div>
					</>
				)}
			</div>
		</section>
	);
}
